import { useEffect, useState } from "react";
import { Award, CheckCircle2, Share2, Eye } from "lucide-react";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { Card, CardContent } from "../components/ui/card.js";

type Totals = Awaited<ReturnType<typeof api.getAnalytics>>["totals"];

/** Issuer analytics dashboard (REQ-1.4): issued / claimed / shares / opens. */
export function AnalyticsPage() {
  const { me } = useAuth();
  const memberships = me?.memberships ?? [];
  const [slug, setSlug] = useState(memberships[0]?.issuerSlug ?? "");
  const [totals, setTotals] = useState<Totals | null>(null);

  useEffect(() => {
    if (!slug) return;
    setTotals(null);
    void api.getAnalytics(slug).then((r) => setTotals(r.totals));
  }, [slug]);

  if (memberships.length === 0) {
    return <p className="text-sm text-muted-foreground">Create an issuer first.</p>;
  }

  const claimPct = totals ? Math.round(totals.claimRate * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Analytics</h1>
        <select
          className="h-9 rounded-[var(--radius)] border border-input bg-card px-3 text-sm"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
        >
          {memberships.map((m) => (
            <option key={m.issuerId} value={m.issuerSlug}>
              {m.issuerName}
            </option>
          ))}
        </select>
      </div>

      {!totals ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-[var(--radius-lg)] bg-secondary" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Metric icon={<Award size={18} />} label="Issued" value={totals.issued.toLocaleString()} />
            <Metric
              icon={<CheckCircle2 size={18} />}
              label="Claimed"
              value={totals.claimed.toLocaleString()}
              sub={`${claimPct}% claim rate`}
            />
            <Metric icon={<Share2 size={18} />} label="Shares" value={totals.shares.toLocaleString()} />
            <Metric
              icon={<Eye size={18} />}
              label="Opens"
              value={totals.opens === null ? "—" : totals.opens.toLocaleString()}
              sub={totals.opens === null ? "needs Analytics Engine" : undefined}
            />
          </div>

          <Card>
            <CardContent className="p-5">
              <h2 className="mb-3 font-semibold">Shares by channel</h2>
              {totals.shares === 0 ? (
                <p className="text-sm text-muted-foreground">No shares yet.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {Object.entries(totals.sharesByChannel).map(([channel, n]) => (
                    <li key={channel} className="flex items-center justify-between">
                      <span className="capitalize">{channel}</span>
                      <span className="font-medium">{n}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Metric({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-muted-foreground">
          {icon}
          <span className="text-sm">{label}</span>
        </div>
        <p className="mt-2 text-3xl font-semibold">{value}</p>
        {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}
